import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface FunnelRow {
  landing_views:    number;
  cta_clicks:       number;
  step1_completed:  number;
  step2_completed:  number;
  email_confirmed:  number;
  map_generated:    number;
  upgrade_clicked:  number;
}

const STEPS = [
  { key: 'landing_views',   label: 'Landing views' },
  { key: 'cta_clicks',      label: 'CTA clicado' },
  { key: 'step1_completed', label: 'Email submetido' },
  { key: 'step2_completed', label: 'Dados de nascimento' },
  { key: 'email_confirmed', label: 'Email confirmado' },
  { key: 'map_generated',   label: 'Mapa gerado' },
  { key: 'upgrade_clicked', label: 'Upgrade clicado' },
] as const;

function rate(num: number, den: number) {
  if (!den) return '-';
  return ((num / den) * 100).toFixed(1) + '%';
}

export function AdminConversaoPage() {
  const [data,    setData]    = useState<FunnelRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('onboarding_funnel')
      .select('*')
      .maybeSingle()
      .then(({ data: row, error: err }) => {
        if (err) setError(err.message);
        else setData(row as FunnelRow | null);
        setLoading(false);
      });
  }, []);

  if (loading) return (
    <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Carregando…</div>
  );
  if (error)   return (
    <div style={{ padding: '2rem', color: '#f87171' }}>Erro: {error}</div>
  );
  if (!data)   return (
    <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>
      Nenhum dado nos últimos 7 dias.
    </div>
  );

  const overall = rate(data.upgrade_clicked, data.landing_views);
  const top     = data.landing_views;

  const dropOffs = [
    { label: 'Landing → CTA',             drop: rate(data.landing_views   - data.cta_clicks,      data.landing_views)   },
    { label: 'CTA → Email',               drop: rate(data.cta_clicks      - data.step1_completed,  data.cta_clicks)      },
    { label: 'Email → Dados nascimento',  drop: rate(data.step1_completed - data.step2_completed,  data.step1_completed) },
    { label: 'Dados → Confirmação email', drop: rate(data.step2_completed - data.email_confirmed,  data.step2_completed) },
  ].sort((a, b) => parseFloat(b.drop) - parseFloat(a.drop));

  return (
    <div style={{ padding: '2rem', maxWidth: '860px' }}>
      <h1 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'var(--text-28)', color: 'var(--bg)', marginBottom: '0.25rem' }}>
        Funil de Conversão
      </h1>
      <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '2rem' }}>
        Últimos 7 dias
      </p>

      {/* Overall rate card */}
      <div style={{
        background:   'var(--bg-elevated)',
        border:       '2px solid var(--petrol)',
        borderRadius: 'var(--radius-md)',
        padding:      '1.25rem 1.5rem',
        marginBottom: '1.5rem',
        display:      'flex',
        alignItems:   'center',
        justifyContent: 'space-between',
        flexWrap:     'wrap',
        gap:          '0.5rem',
      }}>
        <div>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Taxa geral (landing → upgrade)</p>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-38)', fontWeight: 400, color: 'var(--petrol)', margin: 0 }}>
            {overall}
          </p>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>
            {data.upgrade_clicked} upgrades de {data.landing_views} visitantes
          </p>
        </div>
        <span style={{ fontSize: '2rem' }}>📈</span>
      </div>

      {/* Funnel steps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', marginBottom: '2rem' }}>
        {STEPS.map((step, i) => {
          const val  = data[step.key as keyof FunnelRow] as number;
          const prev = i === 0 ? val : data[STEPS[i - 1].key as keyof FunnelRow] as number;
          const pct  = top ? (val / top) * 100 : 0;

          return (
            <div key={step.key} style={{
              background:   'var(--bg-surface)',
              border:       '1px solid var(--line-soft)',
              borderRadius: 'var(--radius-sm)',
              padding:      '0.875rem 1rem',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>{step.label}</span>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--bg)' }}>
                    {val.toLocaleString('pt-BR')}
                  </span>
                  {i > 0 && (
                    <span style={{ fontSize: 'var(--text-caption)', color: 'var(--celeste-soft)', marginLeft: '0.5rem' }}>
                      {rate(val, prev)} de anterior
                    </span>
                  )}
                </div>
              </div>
              <div style={{ height: '3px', borderRadius: '999px', background: 'var(--bg-elevated)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: 'var(--petrol)', transition: 'width 0.4s ease' }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Top drop-offs */}
      <div style={{
        background:   'var(--bg-surface)',
        border:       '1px solid rgba(248,113,113,0.3)',
        borderRadius: 'var(--radius-md)',
        padding:      '1.25rem 1.5rem',
      }}>
        <h2 style={{ fontSize: 'var(--text-15)', fontWeight: 700, color: 'var(--bg)', marginBottom: '0.875rem' }}>
          🚨 Maiores pontos de abandono
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {dropOffs.map(d => (
            <div key={d.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>{d.label}</span>
              <span style={{ fontSize: 'var(--text-label)', fontWeight: 700, color: '#f87171' }}>{d.drop} drop-off</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
